import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaMapMarkerAlt, FaPlus, FaSpinner } from 'react-icons/fa';
import { getApplicationAddresses, saveApplicationAddress } from '../../services/subscriberWalletService';
import { formatAddressType } from '../../utils/formatAddressType';
// The internal pincode service, shared with the hospital and diagnostic address forms — it
// replaced the public India Post API, so do not reach for that one here.
import { lookupPincodeDetail } from '../../services/hospitalService';

// Identity data (DPDP): an address line identifies a subscriber's home. Nothing in here is
// logged, and the id — never the address — is what travels to the order endpoint.

// What the backend serializer marks required (SaveDiagnosticAddressSerializer). Validated here
// too so ops get the field name back instead of a 422 with no focus. The field list mirrors that
// serializer exactly — it has no address_line_2, so neither does this form.
const REQUIRED_FIELDS = {
  address_line_1: 'Address line 1',
  sublocality: 'Locality / area',
  city: 'City',
  state: 'State',
  pincode: 'Pincode',
};

const ADDRESS_TYPES = ['home', 'office', 'other'];

const EMPTY_FORM = {
  address_line_1: '',
  sublocality: '',
  landmark: '',
  city: '',
  state: '',
  pincode: '',
  address_type: 'home',
};

const PINCODE_PATTERN = /^\d{6}$/;

// Some pincode rows carry digits in the district/state text; strip them like the hospital form does.
const cleanCityState = (text) => String(text || '').replace(/\d/g, '').trim();

const formatAddress = (address) => [
  address.address_line_1,
  address.landmark,
  address.sublocality,
  [address.city, address.state].filter(Boolean).join(', '),
  address.pincode,
].filter(Boolean).join(', ');

const inputClass = 'w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';

/**
 * Where this medicine order gets delivered — ops pick one of the subscriber's saved addresses,
 * or add one.
 *
 * The pick is STAGED, not saved: `onSelect` only lifts the id into page state, and it reaches the
 * order with the Save / Create Transaction call. That is deliberate — a live write here would put
 * the subscriber's ECI checkout address on a different footing from every other cart edit on this
 * page (quantity, MRP, out-of-stock are all local until Save). While a staged pick differs from
 * what the order actually holds, the block says so rather than showing a card as chosen.
 *
 * The endpoints behind `getApplicationAddresses` / `saveApplicationAddress` are application-scoped
 * despite their `diagnostic/` URL segment — see the note in subscriberWalletService.
 *
 * Adding an address geocodes server-side (the row is shared with the diagnostic flow, which needs
 * lat/long for serviceability), so the submit is deliberately spinner-gated rather than optimistic.
 */
const DeliveryAddressSelector = ({
  role,
  applicationId,
  selectedAddressId,
  savedAddressId = null,
  hasOrder = false,
  onSelect,
  onNotify,
  disabled = false,
}) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [localities, setLocalities] = useState([]);
  const [pinLookupLoading, setPinLookupLoading] = useState(false);
  const loadedRef = useRef(false);
  // Ops type a pincode a digit at a time, so several lookups can be in flight at once. Only the
  // one for the pincode still in the box may write to the form — otherwise a slow earlier
  // response lands last and overwrites the right city/state with the wrong ones.
  const pinRequestRef = useRef('');

  useEffect(() => {
    if (loadedRef.current || !applicationId || !role) return;
    loadedRef.current = true;

    (async () => {
      try {
        const response = await getApplicationAddresses(role, applicationId);
        const list = response?.metadata?.api_status === true ? (response.msg?.addresses || []) : [];
        setAddresses(list);
      } catch (error) {
        onNotify?.('Failed to load saved addresses', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [applicationId, role, onNotify]);

  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => (prev[field] ? { ...prev, [field]: null } : prev));
  }, []);

  const closeForm = useCallback(() => {
    setShowForm(false);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setLocalities([]);
    pinRequestRef.current = '';
  }, []);

  // Pincode drives the rest of the address: it fills city and state, and lists the localities
  // this pincode covers. All three stay editable — the lookup is a shortcut, not a gate, and ops
  // must still be able to finish the form when a pincode returns nothing.
  const handlePincodeChange = useCallback(async (rawValue) => {
    const pincode = rawValue.replace(/\D/g, '').slice(0, 6);
    updateField('pincode', pincode);
    pinRequestRef.current = pincode;

    if (!PINCODE_PATTERN.test(pincode)) {
      setLocalities([]);
      return;
    }

    setPinLookupLoading(true);
    try {
      const msg = await lookupPincodeDetail(pincode);
      if (pinRequestRef.current !== pincode) return;

      const locations = msg?.locations || [];
      const first = locations[0];
      if (!first) {
        setLocalities([]);
        return;
      }

      // In this API `district` is the city and `city` is the locality (post-office) name — the
      // one mapping worth getting right here, and easy to read backwards.
      const names = [...new Set(locations.map((location) => location.city).filter(Boolean))];
      setLocalities(names);
      setForm((prev) => ({
        ...prev,
        city: cleanCityState(first.district) || prev.city,
        state: cleanCityState(first.state) || prev.state,
        sublocality: names.includes(prev.sublocality) ? prev.sublocality : (names[0] || prev.sublocality),
      }));
      setFormErrors((prev) => ({ ...prev, city: null, state: null, sublocality: null }));
    } catch (error) {
      // A failed lookup must not block the form; ops type the three fields themselves.
      if (pinRequestRef.current === pincode) setLocalities([]);
    } finally {
      if (pinRequestRef.current === pincode) setPinLookupLoading(false);
    }
  }, [updateField]);

  const handleAddAddress = useCallback(async () => {
    const errors = {};
    Object.entries(REQUIRED_FIELDS).forEach(([field, label]) => {
      if (!String(form[field] || '').trim()) errors[field] = `${label} is required`;
    });
    if (!errors.pincode && !PINCODE_PATTERN.test(form.pincode.trim())) {
      errors.pincode = 'Pincode must be 6 digits';
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([field, value]) => [field, String(value).trim()]),
      );
      const response = await saveApplicationAddress(role, { application_id: applicationId, ...payload });

      if (response?.metadata?.api_status === true && response.msg?.address) {
        const created = response.msg.address;
        setAddresses((prev) => [created, ...prev]);
        onSelect(created.id);
        closeForm();
        onNotify?.('Address added. Save the order to apply it.', 'success');
      } else {
        throw new Error(response?.msg?.error || 'Failed to save address');
      }
    } catch (error) {
      onNotify?.(error.message || 'Failed to save address', 'error');
    } finally {
      setSaving(false);
    }
  }, [form, role, applicationId, onSelect, closeForm, onNotify]);

  // Only meaningful when the pick can still be saved: before a provider is chosen there is no
  // order, and on a locked order Save is refused server-side, so telling ops to click it would be
  // a dead end.
  const isUnsavedPick = hasOrder && !disabled
    && Boolean(selectedAddressId) && selectedAddressId !== savedAddressId;

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <FaMapMarkerAlt className="text-blue-600 w-4 h-4" />
          Delivery Address
        </div>
        {!showForm && !disabled && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors"
          >
            <FaPlus className="w-2.5 h-2.5" />
            Add new
          </button>
        )}
      </div>

      {/* The pick is local until Save — say so, so a chosen card is never read as a committed one. */}
      {isUnsavedPick && (
        <p className="mb-3 px-3 py-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md">
          Not saved on this order yet — click Save or Create Transaction to apply it.
        </p>
      )}

      {loading ? (
        <div className="space-y-2">
          {[1, 2].map((row) => (
            <div key={row} className="h-14 bg-gray-100 rounded-md animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {addresses.length === 0 && !showForm && (
            <p className="text-sm text-gray-400">
              No saved addresses for this subscriber. Add one to set a delivery address.
            </p>
          )}

          {addresses.length > 0 && (
            <div role="radiogroup" aria-label="Delivery address" className="space-y-2">
              {addresses.map((address) => {
                const isSelected = selectedAddressId === address.id;
                return (
                  <label
                    key={address.id}
                    className={`flex items-start gap-3 p-3 bg-white border rounded-md transition-colors ${
                      disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                    } ${isSelected ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200 hover:border-blue-300'}`}
                  >
                    <input
                      type="radio"
                      name="delivery-address"
                      className="mt-1 accent-blue-600"
                      value={address.id}
                      checked={isSelected}
                      disabled={disabled}
                      onChange={() => onSelect(address.id)}
                    />
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {address.address_line_1 || 'Address'}
                        </span>
                        {address.address_type && (
                          <span className="shrink-0 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                            {formatAddressType(address.address_type)}
                          </span>
                        )}
                      </span>
                      <span className="block text-sm text-gray-600 leading-relaxed mt-0.5">
                        {formatAddress(address)}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          {showForm && (
            <div className="mt-3 p-3 bg-white border border-gray-200 rounded-md space-y-3">
              <p className="text-sm font-medium text-gray-900">Add new address</p>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Address line 1<span className="text-red-500"> *</span>
                </label>
                <input
                  type="text"
                  value={form.address_line_1}
                  onChange={(e) => updateField('address_line_1', e.target.value)}
                  className={`${inputClass} ${formErrors.address_line_1 ? 'border-red-400' : 'border-gray-300'}`}
                  placeholder="House / flat, building, street"
                />
                {formErrors.address_line_1 && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.address_line_1}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Pincode leads: it fills City and State and lists this pincode's localities. */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Pincode<span className="text-red-500"> *</span>
                  </label>
                  <input
                    type="text"
                    value={form.pincode}
                    onChange={(e) => handlePincodeChange(e.target.value)}
                    inputMode="numeric"
                    maxLength={6}
                    className={`${inputClass} ${formErrors.pincode ? 'border-red-400' : 'border-gray-300'}`}
                    placeholder="6-digit pincode"
                  />
                  {formErrors.pincode ? (
                    <p className="mt-1 text-xs text-red-600">{formErrors.pincode}</p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-400">
                      {pinLookupLoading ? 'Looking up city and state…' : 'Fills city, state and locality'}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Locality / area<span className="text-red-500"> *</span>
                  </label>
                  {localities.length > 0 ? (
                    <select
                      value={form.sublocality}
                      onChange={(e) => updateField('sublocality', e.target.value)}
                      className={`${inputClass} ${formErrors.sublocality ? 'border-red-400' : 'border-gray-300'}`}
                    >
                      <option value="">Select locality</option>
                      {/* A locality typed before the lookup ran must not silently vanish. */}
                      {form.sublocality && !localities.includes(form.sublocality) && (
                        <option value={form.sublocality}>{form.sublocality}</option>
                      )}
                      {localities.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={form.sublocality}
                      onChange={(e) => updateField('sublocality', e.target.value)}
                      className={`${inputClass} ${formErrors.sublocality ? 'border-red-400' : 'border-gray-300'}`}
                      placeholder={pinLookupLoading ? 'Loading localities…' : 'Locality / area'}
                    />
                  )}
                  {formErrors.sublocality && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.sublocality}</p>
                  )}
                </div>

                {[
                  ['city', 'City', true],
                  ['state', 'State', true],
                  ['landmark', 'Landmark', false],
                ].map(([field, label, required]) => (
                  <div key={field}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {label}{required && <span className="text-red-500"> *</span>}
                    </label>
                    <input
                      type="text"
                      value={form[field]}
                      onChange={(e) => updateField(field, e.target.value)}
                      className={`${inputClass} ${formErrors[field] ? 'border-red-400' : 'border-gray-300'}`}
                      placeholder={required ? label : 'Optional'}
                    />
                    {formErrors[field] && <p className="mt-1 text-xs text-red-600">{formErrors[field]}</p>}
                  </div>
                ))}

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Address type</label>
                  <select
                    value={form.address_type}
                    onChange={(e) => updateField('address_type', e.target.value)}
                    className={`${inputClass} border-gray-300`}
                  >
                    {ADDRESS_TYPES.map((type) => (
                      <option key={type} value={type}>{formatAddressType(type)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleAddAddress}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving && <FaSpinner className="w-3 h-3 animate-spin" />}
                  {saving ? 'Saving…' : 'Save address'}
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={saving}
                  className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DeliveryAddressSelector;
